import React, { FC, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react-native";
import DatePicker, {
  DateType,
  useDefaultClassNames,
} from "react-native-ui-datepicker";

import { useTranslate } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { dateAsDayjs, formatDateToIso } from "@/utils/date";
import { cn } from "@/utils/tailwind";

import { VnDialog, VnDialogContent } from "../VnDialog";
import { VnIcon } from "../VnIcon";
import { VnText } from "../VnText/VnText";
import { VnTouchableOpacity } from "../VnTouchableOpacity";

export type BaseDatePickerInputPropsType = {
  i18nKey?: I18nKeyType;
  i18nOptions?: object;
  isDisabled?: boolean;
  hasError?: boolean;
  value?: string;
  onChange: (value: string) => void;
  minDate?: Date;
  maxDate?: Date;
};

export const BaseDatePickerInput: FC<BaseDatePickerInputPropsType> = ({
  i18nKey,
  i18nOptions,
  isDisabled,
  hasError,
  value,
  onChange,
  minDate,
  maxDate,
}) => {
  const [shouldShowPicker, setShouldShowPicker] = useState(false);
  const defaultClassNames = useDefaultClassNames();

  const placeholderText = useTranslate(i18nKey, i18nOptions);

  return (
    <>
      <VnTouchableOpacity
        shouldDebounce={false}
        disabled={isDisabled}
        className={cn(
          "dark:bg-background/50 border-input bg-background mt-0 h-10 justify-center rounded-md border px-3 sm:h-9",
          {
            "border-destructive": hasError,
          },
        )}
        onPress={() => setShouldShowPicker(true)}>
        <VnText
          className={cn("text-foreground", {
            "text-muted-foreground": !value,
          })}>
          {value ? dateAsDayjs(value).format("MMM D, YYYY") : placeholderText}
        </VnText>
      </VnTouchableOpacity>
      <VnDialog open={shouldShowPicker} onOpenChange={setShouldShowPicker}>
        <VnDialogContent>
          <DatePicker
            mode="single"
            date={dateAsDayjs(value).toDate()}
            minDate={minDate ?? dateAsDayjs().toDate()}
            maxDate={maxDate}
            navigationPosition="right"
            weekdaysFormat="short"
            showOutsideDays
            components={{
              IconPrev: (
                <VnIcon
                  as={ChevronLeftIcon}
                  size={20}
                  className="text-primary"
                />
              ),
              IconNext: (
                <VnIcon
                  as={ChevronRightIcon}
                  size={20}
                  className="text-primary"
                />
              ),
            }}
            className="bg-card mt-7"
            styles={{
              years: {
                justifyContent: "flex-start",
              },
              months: {
                justifyContent: "flex-start",
              },
            }}
            classNames={{
              ...defaultClassNames,
              month_selector_label:
                "text-xl font-primary-medium leading-md text-foreground",
              year_selector_label:
                "text-xl font-primary-medium leading-md text-foreground",
              selected: "bg-primary text-primary-foreground",
              weekday_label: "text-muted-foreground",
              today: "bg-card border border-primary",
              today_label: "text-primary",
              outside_label: "text-muted-foreground",
            }}
            onChange={({ date }: { date: DateType }) => {
              onChange(formatDateToIso(date));
              setShouldShowPicker(false);
            }}
          />
        </VnDialogContent>
      </VnDialog>
    </>
  );
};
